/**
 * @fileoverview Bills List Page
 * @task B.INV.2 - Accounts Payable Bill Management
 * @theme Polished Corporate - Teal accents, refined shadows
 *
 * Features:
 * - Summary cards (Total Outstanding, Due This Week, Overdue, Paid This Month)
 * - Filter bar (status, vendor, search)
 * - Data table with sorting and pagination
 * - Action buttons (View, Edit, Pay, Approve)
 * - AI-powered bill extraction indicator
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@lib/utils';
import { Button } from '@components/ui/button';
import { BillSummaryCards } from '@components/bills/BillSummaryCards';
import { BillStatusBadge } from '@components/bills/BillStatusBadge';
import { DataTable, type Column } from '@components/common/DataTable';
import { useMarket } from '@contexts/MarketContext';
import { useMarketApi } from '../../hooks/useMarketApi';
import type { Bill, BillStatus } from '@lib/demo-data';

// ============================================================================
// Types
// ============================================================================

type SortableField = keyof Bill;

interface FilterState {
  status?: BillStatus | 'all' | undefined;
  vendorId?: string | undefined;
  search?: string | undefined;
  sortBy: SortableField;
  sortOrder: 'asc' | 'desc';
}

// ============================================================================
// Filter Bar Component
// ============================================================================

interface FilterBarProps {
  filter: FilterState;
  onFilterChange: (filter: Partial<FilterState>) => void;
  t: (key: string) => string;
  isBrazil: boolean;
  vendors: Array<{ id: string; name: string }>;
}

function FilterBar({ filter, onFilterChange, t: _t, isBrazil, vendors }: FilterBarProps): JSX.Element {
  const statusOptions: { value: BillStatus | 'all'; label: string }[] = isBrazil
    ? [
        { value: 'all', label: 'Todos os Status' },
        { value: 'draft', label: 'Rascunho' },
        { value: 'pending', label: 'Pendente' },
        { value: 'approved', label: 'Aprovado' },
        { value: 'paid', label: 'Pago' },
        { value: 'overdue', label: 'Vencido' },
      ]
    : [
        { value: 'all', label: 'All Statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
      ];

  return (
    <div className="flex flex-col gap-3 sm:gap-4 rounded-xl bg-white p-3 sm:p-4 shadow-card">
      <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:flex-wrap">
        {/* Search Input - Full width on mobile */}
        <div className="relative flex-1 min-w-0 sm:max-w-xs">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={isBrazil ? 'Buscar faturas...' : 'Search bills...'}
            value={filter.search ?? ''}
            onChange={(e) => onFilterChange({ search: e.target.value })}
            className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50/50 pl-10 pr-4 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
          />
        </div>

        {/* Filters row - Side by side on mobile */}
        <div className="flex gap-2 sm:gap-3">
          {/* Status Filter */}
          <select
            value={filter.status ?? 'all'}
            onChange={(e) =>
              onFilterChange({ status: e.target.value as BillStatus | 'all' })
            }
            className="h-11 flex-1 sm:flex-none rounded-xl border border-gray-200 bg-gray-50/50 px-2 sm:px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Vendor Filter - Hidden on small mobile */}
          <select
            value={filter.vendorId ?? ''}
            onChange={(e) => onFilterChange({ vendorId: e.target.value || undefined })}
            className="h-11 flex-1 sm:flex-none rounded-xl border border-gray-200 bg-gray-50/50 px-2 sm:px-3 text-sm transition-all focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 hidden xs:block"
          >
            <option value="">{isBrazil ? 'Fornecedor' : 'Vendor'}</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Clear Filters - Touch-friendly size */}
      {(filter.search || filter.status !== 'all' || filter.vendorId) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onFilterChange({
              search: '',
              status: 'all',
              vendorId: undefined,
            })
          }
          className="self-start text-gray-500 hover:text-teal-600 hover:bg-teal-50 min-h-[44px] px-3"
        >
          <svg className="mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          {isBrazil ? 'Limpar' : 'Clear'}
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Action Menu Component
// ============================================================================

interface ActionMenuProps {
  bill: Bill;
  onView: (bill: Bill) => void;
  onEdit: (bill: Bill) => void;
  onPay: (bill: Bill) => void;
  onApprove: (bill: Bill) => void;
  isBrazil?: boolean;
}

function ActionMenu({
  bill,
  onView,
  onEdit,
  onPay,
  onApprove,
  isBrazil = false,
}: ActionMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="rounded-xl p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 transition-all hover:bg-teal-50 hover:text-teal-600"
        aria-label={isBrazil ? 'Ações' : 'Actions'}
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />

          {/* Menu */}
          <div className="absolute right-0 top-full z-50 mt-1 w-40 rounded-xl bg-white py-1 shadow-elevated">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(bill);
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-teal-50 hover:text-teal-700"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {isBrazil ? 'Visualizar' : 'View'}
            </button>

            {bill.status !== 'paid' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(bill);
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-teal-50 hover:text-teal-700"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {isBrazil ? 'Editar' : 'Edit'}
              </button>
            )}

            {(bill.status === 'draft' || bill.status === 'pending') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove(bill);
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-teal-600 transition-colors hover:bg-teal-50 hover:text-teal-700"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {isBrazil ? 'Aprovar' : 'Approve'}
              </button>
            )}

            {(bill.status === 'approved' || bill.status === 'overdue') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPay(bill);
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {isBrazil ? 'Pagar' : 'Pay'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Bills Page Component
// ============================================================================

/**
 * Bills Page
 * Displays a list of bills with filtering, sorting, and pagination
 */
export function BillsPage(): JSX.Element {
  const navigate = useNavigate();
  const { t, formatCurrency, formatDate, isBrazil, market } = useMarket();
  const { billsApi, vendorsApi } = useMarketApi();

  // State
  const [bills, setBills] = useState<Bill[]>([]);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState<FilterState>({
    status: 'all',
    sortBy: 'dueDate',
    sortOrder: 'asc',
  });

  // Fetch vendors for filter
  useEffect(() => {
    const loadVendors = async () => {
      const result = await vendorsApi.getVendors();
      setVendors(result.data.map((v) => ({ id: v.id, name: v.name })));
    };
    void loadVendors();
  }, [market, vendorsApi]);

  // Fetch bills
  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const status = filter.status === 'all' ? undefined : filter.status;
      const result = await billsApi.getBills({
        ...(status ? { status } : {}),
        ...(filter.search ? { search: filter.search } : {}),
        page,
        limit: pageSize,
      });
      setBills(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to fetch bills:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filter, page, pageSize, billsApi]);

  useEffect(() => {
    void fetchBills();
  }, [fetchBills]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter.status, filter.vendorId, filter.search]);

  // Handlers
  const handleFilterChange = (updates: Partial<FilterState>): void => {
    setFilter((prev) => ({ ...prev, ...updates }));
  };

  const handleSort = (column: string, order: 'asc' | 'desc'): void => {
    setFilter((prev) => ({
      ...prev,
      sortBy: column as SortableField,
      sortOrder: order,
    }));
  };

  const handleViewBill = (bill: Bill): void => {
    console.log('View bill:', bill.id);
  };

  const handleEditBill = (bill: Bill): void => {
    console.log('Edit bill:', bill.id);
  };

  const handleApproveBill = async (bill: Bill): Promise<void> => {
    // TODO: Implement bill status update in API
    console.log('Approve bill:', bill.id, '-> approved');
    void fetchBills();
  };

  const handlePayBill = async (bill: Bill): Promise<void> => {
    // TODO: Implement bill status update in API
    console.log('Pay bill:', bill.id, '-> paid');
    void fetchBills();
  };

  // Table columns - Mobile responsive with hidden columns
  const columns: Column<Bill>[] = [
    {
      key: 'billNumber',
      header: isBrazil ? 'Fatura #' : 'Bill #',
      sortable: true,
      render: (bill) => (
        <span className="font-semibold text-teal-600">{bill.billNumber}</span>
      ),
    },
    {
      key: 'vendorName',
      header: isBrazil ? 'Fornecedor' : 'Vendor',
      sortable: true,
      render: (bill) => {
        const itemCount = bill.lineItems?.length ?? 1;
        return (
          <div>
            <p className="font-medium truncate max-w-[120px] sm:max-w-none">{bill.vendorName}</p>
            <p className="text-xs text-muted-foreground">
              {itemCount} {isBrazil ? (itemCount > 1 ? 'itens' : 'item') : (itemCount > 1 ? 'items' : 'item')}
            </p>
          </div>
        );
      },
    },
    {
      key: 'amount',
      header: isBrazil ? 'Valor' : 'Amount',
      sortable: true,
      align: 'right',
      render: (bill) => (
        <span className="font-medium">{formatCurrency(bill.amount)}</span>
      ),
    },
    {
      key: 'billDate',
      header: isBrazil ? 'Data' : 'Date',
      sortable: true,
      className: 'hidden md:table-cell', // Hidden on mobile
      render: (bill) => formatDate(bill.billDate),
    },
    {
      key: 'dueDate',
      header: isBrazil ? 'Venc.' : 'Due',
      sortable: true,
      className: 'hidden sm:table-cell', // Hidden on small mobile
      render: (bill) => {
        const isOverdue = bill.status === 'overdue';
        const dueDate = new Date(bill.dueDate);
        const today = new Date();
        const daysUntilDue = Math.ceil(
          (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );

        return (
          <div>
            <p className={cn(isOverdue && 'font-medium text-red-600')}>
              {formatDate(bill.dueDate)}
            </p>
            {bill.status !== 'paid' && bill.status !== 'draft' && (
              <p
                className={cn(
                  'text-xs',
                  isOverdue
                    ? 'text-red-600'
                    : daysUntilDue <= 7
                    ? 'text-yellow-600'
                    : 'text-muted-foreground'
                )}
              >
                {isOverdue
                  ? isBrazil ? `${Math.abs(daysUntilDue)}d atraso` : `${Math.abs(daysUntilDue)}d late`
                  : daysUntilDue === 0
                  ? isBrazil ? 'Hoje' : 'Today'
                  : isBrazil ? `${daysUntilDue}d` : `${daysUntilDue}d`}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (bill) => <BillStatusBadge status={bill.status} isBrazil={isBrazil} />,
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      align: 'center',
      render: (bill) => (
        <ActionMenu
          bill={bill}
          onView={handleViewBill}
          onEdit={handleEditBill}
          onPay={() => void handlePayBill(bill)}
          onApprove={() => void handleApproveBill(bill)}
          isBrazil={isBrazil}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50/30">
      {/* Main Content - Responsive padding */}
      <div className="container px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Action Header - Mobile responsive */}
        <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm sm:text-base text-gray-500">
            {isBrazil ? 'Gerenciar faturas e pagamentos' : 'Manage bills and payments'}
          </p>
          <Button
            onClick={() => navigate('/accounts-payable/bills/create')}
            className="gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm transition-all hover:from-teal-600 hover:to-teal-700 hover:shadow-md min-h-[44px] w-full sm:w-auto"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {isBrazil ? 'Nova Fatura' : 'New Bill'}
          </Button>
        </div>
        {/* Summary Cards */}
        <BillSummaryCards className="mb-4 sm:mb-6" isBrazil={isBrazil} formatCurrency={formatCurrency} />

        {/* Filter Bar */}
        <FilterBar
          filter={filter}
          onFilterChange={handleFilterChange}
          t={t}
          isBrazil={isBrazil}
          vendors={vendors}
        />

        {/* Bills Table */}
        <div className="mt-4 overflow-hidden rounded-xl bg-white shadow-card">
          <DataTable
            data={bills}
            columns={columns}
            keyField="id"
            isLoading={isLoading}
            emptyMessage={isBrazil ? 'Nenhuma fatura encontrada. Tente ajustar os filtros ou crie uma nova fatura.' : 'No bills found. Try adjusting your filters or create a new bill.'}
            sortBy={filter.sortBy}
            sortOrder={filter.sortOrder}
            onSort={handleSort}
            pagination={{
              page,
              pageSize,
              total,
              totalPages,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            }}
            rowClassName={(bill) =>
              cn(bill.status === 'overdue' && 'bg-red-50/50')
            }
          />
        </div>
      </div>
    </div>
  );
}

export default BillsPage;
